import type { Fraction } from '../../util/fractionType';
import { frac, multiplyFractions } from '../../util/integerMath';
import type { FuzzyValue } from '../../util/fuzzyNumbers';
import { fractionToFuzzy, multiplyFuzzy } from '../../util/fuzzyMath';
import { invariant } from '../../util/invariant';
import type { ResourceLocation } from '../../io/resource';
import type { ClipRewrapInfo } from '../clipRewrapInfo';
import type { LocalMediaTiming } from '../../types/localMediaPathProvider';

/**
 *  Provides all the information that is required from an item in order to use
 *  it in an MXF rewrap operation
 */
export interface MxfRewrapItem {
  id: string,
  highRes: ResourceLocation,
  index: ResourceLocation,
  frameRate: FuzzyValue,
  compatibilityHash: string | null,
  origin: number,
  // The time range that inPoint/outPoint should be computed against. Equal to the request's
  // own range when resolved to a complete file, but adjusted to be relative to the specific
  // retrieved segment's own start when the item's highres is only partially retrieved
  // (external highres, offline storage restore) - that file's frame 0 is not the item's frame 0.
  effectiveTiming: LocalMediaTiming,
}

// A caller either already knows the exact seconds it needs (seconds_range), or only knows a
// segment index range and needs the item's own segmentDuration to convert it (segment_range) -
// which the provider resolves internally, since it already has the item in hand.
export type MxfRewrapItemRange =
  | { type: 'segment_range', first: number, last: number }
  | { type: 'seconds_range', inPointInSeconds: Fraction, outPointInSeconds: Fraction };

export interface MxfRewrapItemRequest {
  itemId: string,
  range: MxfRewrapItemRange,
}

export interface MxfRewrapItemProvider {
  getRewrapItems(requests: MxfRewrapItemRequest[]): Promise<MxfRewrapItem[]>;
}

// Resolves a range request into concrete seconds, converting a segment-index range using the
// item's own segmentDuration - the one piece of item-specific knowledge a segment_range
// request needs, which every MxfRewrapItemProvider implementation has in hand right after
// fetching the item, so callers never need a separate round trip just to learn it.
export function resolveMxfRewrapItemRange(
  range: MxfRewrapItemRange,
  segmentDuration: Fraction | null | undefined,
  itemId: string,
): LocalMediaTiming {
  if (range.type === 'seconds_range') {
    return { startInSeconds: range.inPointInSeconds, endInSeconds: range.outPointInSeconds };
  }
  if (segmentDuration === null || segmentDuration === undefined) {
    throw new Error(`Item ${itemId} is not compatible with MXF rewrap: no segmentDuration to resolve a segment range`);
  }
  return {
    startInSeconds: multiplyFractions(frac(range.first, 1), segmentDuration),
    endInSeconds: multiplyFractions(frac(range.last, 1), segmentDuration),
  };
}

export interface MxfRewrapInput {
  highRes: ResourceLocation
  index: ResourceLocation,
  inPoint: number,
  outPoint: number,
  compatibilityHash: string | null,
  origin: number,
}

export async function tsSegmentRangeToMxfRewrapInput(
  services: {
    mxfRewrapItemProvider: MxfRewrapItemProvider,
  },
  itemId: string,
  startSegmentTime: number,
  endSegmentTime: number,
): Promise<MxfRewrapInput> {
  const { mxfRewrapItemProvider } = services;
  const [item] = await mxfRewrapItemProvider.getRewrapItems([{
    itemId,
    range: { type: 'segment_range', first: startSegmentTime, last: endSegmentTime },
  }]);

  invariant(item !== undefined, 'expected item');

  const inPoint = Math.round(multiplyFuzzy(fractionToFuzzy(item.effectiveTiming.startInSeconds), item.frameRate).approximate);
  const outPoint = Math.round(multiplyFuzzy(fractionToFuzzy(item.effectiveTiming.endInSeconds), item.frameRate).approximate);

  return {
    highRes: item.highRes,
    index: item.index,
    inPoint,
    outPoint,
    compatibilityHash: item.compatibilityHash,
    origin: item.origin,
  };
}

export async function clipListToMxfRewrapInputs(
  services: {
    mxfRewrapItemProvider: MxfRewrapItemProvider,
  },
  clips: ClipRewrapInfo[],
): Promise<MxfRewrapInput[]> {
  const { mxfRewrapItemProvider } = services;
  // Requested (and resolved) per clip, not deduped by itemId: an item whose highres is only
  // partially retrieved can resolve to a different file for each clip's range, even when
  // multiple clips reference the same item.
  const requests : MxfRewrapItemRequest[] = clips.map((clip) => ({
    itemId: clip.itemId,
    range: { type: 'seconds_range', inPointInSeconds: clip.inPointInSeconds, outPointInSeconds: clip.outPointInSeconds },
  }));
  const items = await mxfRewrapItemProvider.getRewrapItems(requests);
  invariant(items.length === clips.length, 'expected one resolved item per clip');

  const inputs : MxfRewrapInput[] = [];
  for (const item of items) {
    const inPoint = Math.round(multiplyFuzzy(fractionToFuzzy(item.effectiveTiming.startInSeconds), item.frameRate).approximate);
    const outPoint = Math.round(multiplyFuzzy(fractionToFuzzy(item.effectiveTiming.endInSeconds), item.frameRate).approximate);
    inputs.push({
      inPoint,
      outPoint,
      highRes: item.highRes,
      index: item.index,
      compatibilityHash: item.compatibilityHash,
      origin: item.origin,
    });
  }
  return inputs;
}
